'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { NavbarAuth } from './NavbarAuth';
import { CurrencyDropdown } from './CurrencyDropdown';
import { NavbarLinks } from './NavbarLinks';
import { IMG } from '../imgPrefix';

/**
 * Site header — sandbox layout (sports-toggle mobile hamburger + `desktop-sports-list`) with
 * ag-local's hotels-route logic grafted on:
 *   - currency picker also hidden on the hotel booking flow (/hotels/book/*),
 *   - mobile drawer auto-closes on route change (Navbar is mounted by the root layout, so it
 *     never unmounts on client navigations),
 *   - the white-label embed (/booking-engine-landing) renders NO site chrome.
 *
 * Currency picker is suppressed on the sports checkout flow + booking-success page (booking is
 * locked to its persisted display currency; switching mid-flow would desync Stripe / the totals).
 */
export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname() || '';
  const isCheckoutRoute = /^\/sports\/[^/]+\/checkout(\/|$)/.test(pathname);
  const isBookingSuccessRoute = /^\/sports\/booking\/[^/]+\/success(\/|$)/.test(pathname);
  // ag-local: our hotel booking flow lives under /hotels/book/* (guest, payment, confirmation,
  // booking-failed) — hide the currency picker there too (CMS common.js invalidPaths parity).
  const isHotelBookingFlow = pathname.startsWith('/hotels/book');
  const showCurrency = !isCheckoutRoute && !isBookingSuccessRoute && !isHotelBookingFlow;

  // Close the mobile drawer on every route change (root-layout-mounted → no unmount on nav).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // White-label plugin embed (/booking-engine-landing) is iframe content — render NO site chrome.
  if (pathname.startsWith('/booking-engine-landing')) return null;

  return (
    <header className="w-full">
      <div className="mx-auto px-2">
        <nav className="flex items-center justify-between py-4">
          {/* LOGO — smaller on mobile, taller from md up. */}
          <a href="/" className="flex items-center">
            <img
              src={`${IMG}/image/purple-new_logo.png`}
              alt="BookItPlease"
              className="h-5 md:h-10"
            />
          </a>

          {/* RIGHT SIDE — desktop menu + currency + profile + hamburger */}
          <div className="flex items-center space-x-2">
            {/* Desktop primary nav — hidden below lg. The same item set renders again inside the
                mobile drawer below, so NavbarLinks is the single source of truth for links + auth-gating. */}
            <ul id="mainMenu" className="hidden lg:flex items-center desktop-sports-list">
              <NavbarLinks variant="desktop" />
            </ul>

            {showCurrency && <CurrencyDropdown />}

            <NavbarAuth />

            {/* MOBILE HAMBURGER — visible below lg; toggles the drawer below. */}
            <button
              type="button"
              id="navToggle"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              className="lg:hidden flex flex-col space-y-1 sports-toggle-icon rounded p-2"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="w-6 h-0.5 bg-white block" />
              <span className="w-6 h-0.5 bg-white block" />
              <span className="w-6 h-0.5 bg-white block" />
            </button>
          </div>
        </nav>

        {/* MOBILE DRAWER — same links as the desktop <ul>, stacked. */}
        <div
          id="mobileMenu"
          className={`${menuOpen ? 'block' : 'hidden'} bg-white px-2 py-2 lg:hidden`}
        >
          <ul className="space-y-2">
            <NavbarLinks variant="mobile" />
          </ul>
        </div>
      </div>
    </header>
  );
}
